// /app/frontend/src/components/frugal-v2/components/ReportsHistoryModal.jsx
// Closed-months history with view + PDF download (<300 lines)

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Download, ChevronRight, ArrowLeft, History } from 'lucide-react';
import { fetchClosingHistory } from '../services/frugalApi';
import { useAuth } from '../hooks/useAuth';
import { generateClosingPDF } from './reports/generateClosingPDF';
import ClosingDetailView from './reports/ClosingDetailView';
import { toast } from 'sonner';

const ReportsHistoryModal = ({ isOpen, onClose, t, currentLanguage = 'es' }) => {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setLoading(true);
    setSelected(null);
    fetchClosingHistory(token)
      .then((res) => {
        if (!alive) return;
        setList(Array.isArray(res?.history) ? res.history : []);
      })
      .catch(() => {
        if (!alive) return;
        toast.error(t('frugal.reportsHistory.loadError'));
        setList([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [isOpen, token, t]);

  const handleDownload = async (closing, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    try {
      await generateClosingPDF(closing, t, currentLanguage);
    } catch (err) {
      toast.error('PDF error');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
      <div
        data-testid="reports-history-modal"
        className="bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {selected ? (
              <button
                onClick={() => setSelected(null)}
                className="p-1 -ml-1 rounded hover:bg-white/10"
                data-testid="reports-history-back"
              >
                <ArrowLeft className="w-5 h-5 text-slate-300" />
              </button>
            ) : (
              <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <History className="w-5 h-5 text-purple-300" />
              </div>
            )}
            <div>
              <h2 className="text-white font-semibold text-base">
                {selected
                  ? selected.month_display || selected.month_key
                  : t('frugal.reportsHistory.title')}
              </h2>
              {!selected && (
                <p className="text-xs text-slate-500">{t('frugal.reportsHistory.subtitle')}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10"
            data-testid="reports-history-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">
              {t('frugal.reportsHistory.loading')}
            </div>
          )}

          {!loading && !selected && list.length === 0 && (
            <div className="px-5 py-10 text-center">
              <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">{t('frugal.reportsHistory.empty')}</p>
            </div>
          )}

          {/* List view */}
          {!loading && !selected && list.length > 0 && (
            <ul className="divide-y divide-white/5">
              {list.map((c) => {
                const isPartial = !c?.summary?.schema_version || c.summary.schema_version < 2;
                return (
                  <li
                    key={c.id}
                    className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/5 cursor-pointer"
                    onClick={() => setSelected(c)}
                    data-testid={`reports-history-item-${c.month_key}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-emerald-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {c.month_display || c.month_key}
                      </p>
                      {isPartial && (
                        <p className="text-[10px] text-amber-400/80 mt-0.5">
                          {t('frugal.reportsHistory.onlyCategoriesAvailable')}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleDownload(c, e)}
                      className="p-2 rounded-lg bg-white/5 hover:bg-purple-500/20 text-slate-300 hover:text-purple-200 transition-colors"
                      title={t('frugal.reportsHistory.downloadPdf')}
                      data-testid={`reports-history-pdf-${c.month_key}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </li>
                );
              })}
            </ul>
          )}

          {/* Detail view */}
          {!loading && selected && (
            <ClosingDetailView closing={selected} t={t} onDownload={handleDownload} />
          )}
        </div>

        {/* Footer note */}
        {!selected && !loading && (
          <div className="px-5 py-3 border-t border-white/5 text-[11px] text-slate-500 text-center">
            {t('frugal.reportsHistory.retentionNote')}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ReportsHistoryModal;
